import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import i18next from 'i18next';
import Cookies from 'js-cookie';
import { css } from '@emotion/core';
import { Box, Typography } from '@material-ui/core';
import ShareButton from 'components/common/Button/ShareButton';
import { promo500 } from 'Mui/styles/color';
import { updateSettingStyle } from 'client/redux/modules/settingStyle';
import useGetReferralData from 'client/hooks/useGetReferralData';
import { resultFooterContainerStyle, shareButtonWrapperStyle } from 'client/styles/common';
import { getShareInfoApi } from 'api/client';
import { defaultReferralShareCard, defaultReferralButton } from 'config/promoDefaultSettingValue';
import { isCustomResultGames } from 'config/promoGameTypes';
import { SHARE_CARD_IMAGE_TYPES } from 'editor/game_mgmShare/utils/constants';
import {
    parseContentDynamicParameters,
    convertLegacyToPromo,
    convertToClientData,
    convertApiResponseToPromoData,
    API_REQUIRED_PARAMS
} from 'hooks/usePromoDynamicParameters';
import ResultPageFroalaView from 'components/ResultPage/ResultPageFroalaView';
import useResultPage from 'hooks/useResultPage';

function ResultFooter({ propsReferralCount = 0 }) {
    const dispatch = useDispatch();
    const { uuid: promoUUID, id: promoId, type: gameType, name: promoTitle } = useSelector(
        s => s.promo
    );
    const { isMgmShare = false } = useSelector(s => s.flags);
    const { referralShareCard: styleReferralShareCard = {}, referralButton = {} } = useSelector(
        s => s.settingStyle
    );
    const { referralShareCard: mgmReferralShareCard = {} } = useSelector(s => s.settingMGMReferral);
    const entriesList = useSelector(s => s.userGameResultRecords);
    const { isLogin } = useSelector(s => s.userLoginState);
    const userGameResultRecords = useSelector(s => s.userGameResultRecords);
    const { currentEntryId = '' } = useSelector(s => s.userEntryState);
    const { metaTitle, ogImageUrl } = useSelector(s => s.settingPromo);
    const [enableFetchReferralInfo, setEnableFetchReferralInfo] = useState(false);
    const [isFetchingData, setIsFetchingData] = useState(false);
    const [dynamicTextData, setDynamicTextData] = useState({
        processedTitle: '',
        processedDesc: '',
        processedButtonText: '',
        isProcessing: false
    });
    const [customResultData, setCustomResultData] = useState({
        title: null,
        image: null,
        username: ''
    });
    const latestEntry = entriesList[entriesList.length - 1];
    const { referralCode, referralUrl, referralCount, isLoading } = useGetReferralData({
        promoUUID,
        promoId,
        latestEntryId: latestEntry?.id,
        latestEntryUuid: latestEntry?.uuid,
        // dependency 都要是 true hook 才會在一載入時跑 useEffect 去打 referral api 拿 referralCode, referralUrl, referralCount
        dependency: [enableFetchReferralInfo]
    });
    const showReferralCount = referralCount > 0 || propsReferralCount > 0;
    const resultPage = useResultPage();
    const { referralShareFroalaEditor, shareSlotEnabled = true } = resultPage;
    const referralShareCard = useMemo(
        () =>
            Object.keys(mgmReferralShareCard).length > 0
                ? mgmReferralShareCard
                : styleReferralShareCard,
        [styleReferralShareCard, mgmReferralShareCard]
    );

    // https://app.clickup.com/36226806/v/dc/12hhqp-20018
    const displayReferralTitle = useMemo(() => {
        const defaultTitle = metaTitle || promoTitle || '';

        if (!referralShareCard?.invitationTitle) {
            return defaultTitle;
        }

        if (!isCustomResultGames(gameType)) {
            return dynamicTextData.processedTitle || referralShareCard.invitationTitle;
        }

        return dynamicTextData.processedTitle || defaultTitle;
    }, [
        referralShareCard?.invitationTitle,
        gameType,
        metaTitle,
        promoTitle,
        dynamicTextData.processedTitle
    ]);

    const displayReferralDesc = useMemo(() => {
        if (!referralShareCard?.invitationDesc) {
            return '';
        }

        if (!isCustomResultGames(gameType)) {
            return dynamicTextData.processedDesc || referralShareCard.invitationDesc;
        }

        return dynamicTextData.processedDesc || '';
    }, [referralShareCard?.invitationDesc, gameType, dynamicTextData.processedDesc]);

    const displayReferralImage = useMemo(() => {
        if (
            referralShareCard?.resultImageType === SHARE_CARD_IMAGE_TYPES.CUSTOM ||
            !isCustomResultGames(gameType)
        ) {
            const finalImageUrl = referralShareCard?.imageUrl || ogImageUrl;
            return {
                isShowImage: referralShareCard?.isShowImage ?? Boolean(finalImageUrl),
                imageUrl: finalImageUrl
            };
        }

        if (Boolean(customResultData?.image)) {
            return {
                isShowImage: referralShareCard?.isShowImage ?? Boolean(customResultData.image),
                imageUrl: customResultData.image
            };
        }
        return {
            isShowImage: false,
            imageUrl: ''
        };
    }, [
        customResultData?.image,
        referralShareCard?.imageUrl,
        referralShareCard?.resultImageType,
        gameType,
        referralShareCard?.isShowImage,
        ogImageUrl
    ]);

    const config = {
        variant: 'contained',
        isFixedWindow: true,
        dialogTextColor: referralShareCard?.themeTextColor,
        dialogBgColor: referralShareCard?.themeBgColor,
        enableLineLiff: referralShareCard?.enableLineLiff,
        enableDisplayShareCardButton: isMgmShare,
        enableDisplayShareCardImage: displayReferralImage.isShowImage,
        enableDisplayShareCardReferralCode: false,
        shareCardTitle: displayReferralTitle,
        shareCardDesc: displayReferralDesc,
        shareCardImageUrl: displayReferralImage.imageUrl,
        shareCardButtonText: dynamicTextData.processedButtonText || referralShareCard?.buttonText,
        shareCardButtonTextColor: referralShareCard?.buttonTextColor,
        shareCardButtonBgColor: referralShareCard?.buttonBgColor,
        isLoadingReferral: isLoading || dynamicTextData.isProcessing,
        referralCode,
        shareLinkUrl: referralUrl,
        shareChannel: referralShareCard?.shareChannel,
        resultImageType: referralShareCard?.resultImageType,
        isShowImage: false
    };

    // 檢查是否需要調用 API 的函數
    const needsApiCall = useCallback(() => {
        const textsToCheck = [
            referralShareCard?.invitationTitle,
            referralShareCard?.invitationDesc,
            referralShareCard?.buttonText
        ].filter(Boolean);

        if (textsToCheck.length === 0) return false;

        // 檢查是否包含需要 API 的動態參數
        for (const text of textsToCheck) {
            const paramIds = parseContentDynamicParameters(text); // enableLegacyConversion = true
            const allParams = [...paramIds.promo, ...paramIds.legacy];

            // 使用導出的 API_REQUIRED_PARAMS 檢查
            const apiRequiredParams = allParams.filter(param => API_REQUIRED_PARAMS.has(param));

            if (apiRequiredParams.length > 0) {
                return true;
            }
        }

        return false;
    }, [referralShareCard]);

    // 統一的參數處理函數
    const processParameters = useCallback(
        (apiResponse = null) => {
            try {
                setDynamicTextData(prev => ({ ...prev, isProcessing: true }));

                let promoData = {};

                // 如果有 API 回應，則處理動態參數
                if (apiResponse) {
                    // 收集所有需要的參數
                    const textsToCheck = [
                        referralShareCard?.invitationTitle,
                        referralShareCard?.invitationDesc,
                        referralShareCard?.buttonText
                    ].filter(Boolean);

                    const allRequiredParams = new Set();
                    textsToCheck.forEach(text => {
                        const paramIds = parseContentDynamicParameters(text);
                        [...paramIds.promo, ...paramIds.legacy].forEach(param =>
                            allRequiredParams.add(param)
                        );
                    });

                    // 使用導出的函數轉換 API 回應
                    promoData = convertApiResponseToPromoData(
                        apiResponse,
                        Array.from(allRequiredParams)
                    );
                }

                // 處理各個文本字段
                const processText = originalText => {
                    if (!originalText) return '';

                    // 首先轉換舊格式為新格式
                    const convertedText = convertLegacyToPromo(originalText);

                    // 然後使用 promo 數據替換（如果有的話）
                    return convertToClientData(convertedText, promoData);
                };

                setDynamicTextData({
                    processedTitle: processText(referralShareCard?.invitationTitle),
                    processedDesc: processText(referralShareCard?.invitationDesc),
                    processedButtonText: processText(referralShareCard?.buttonText),
                    isProcessing: false
                });
            } catch (error) {
                console.error('Error processing parameters:', error);
                setDynamicTextData(prev => ({ ...prev, isProcessing: false }));
            }
        },
        [referralShareCard]
    );

    const getShareInfoData = useCallback(async () => {
        try {
            setIsFetchingData(true);
            const { payload } = await getShareInfoApi({
                promoUUID,
                entryUUID: latestEntry?.uuid
            });
            const { shareMemberFullname, share } = payload;

            // 更新自定義結果數據
            setCustomResultData({
                ...(share || { title: null, image: null }),
                username: shareMemberFullname || ''
            });

            // 處理動態參數
            processParameters(payload);
        } catch (err) {
            console.error(err);
            // 如果 API 調用失敗，至少處理靜態參數
            processParameters();
        } finally {
            setIsFetchingData(false);
        }
    }, [latestEntry?.uuid, promoUUID, processParameters]);

    const handleShareButtonClick = () => {
        setEnableFetchReferralInfo(true);
        // 已經優先點擊分享邀請資訊dialog 就不再跳邀請提示
        const hasReferralNotifyEntry = JSON.parse(Cookies.get('hasReferralNotifyEntry') || '[]');
        const existEntryUuid = hasReferralNotifyEntry.find(
            entryId =>
                Boolean(userGameResultRecords.find(({ id }) => id === entryId)) ||
                currentEntryId === entryId
        );

        if (!existEntryUuid) {
            Cookies.set(
                'hasReferralNotifyEntry',
                JSON.stringify([...hasReferralNotifyEntry, currentEntryId])
            );
        }

        // 檢查是否需要調用 API
        if (needsApiCall() && (isLogin || isCustomResultGames(gameType))) {
            getShareInfoData();
        } else {
            // 不需要 API 調用，只處理靜態參數轉換
            processParameters();
        }
    };

    useEffect(() => {
        // [860t0duz4] 若 MGM phrase 2 前的建立的活動沒有邀請按鈕與邀請資訊的預設值，系統要幫他自動補上
        if (Object.keys(referralShareCard).length === 0) {
            dispatch(
                updateSettingStyle({
                    referralShareCard: {
                        ...referralShareCard,
                        ...defaultReferralShareCard(gameType).referralShareCard
                    }
                })
            );
        }
    }, [dispatch, referralShareCard, gameType]);

    useEffect(() => {
        // [860t0duz4] 若 MGM phrase 2 前的建立的活動沒有邀請按鈕與邀請資訊的預設值，系統要幫他自動補上
        if (Object.keys(referralButton).length === 0) {
            dispatch(
                updateSettingStyle({
                    referralButton: {
                        ...defaultReferralButton(true).referralButton
                    }
                })
            );
        }
    }, [dispatch, referralButton, gameType]);

    //[86et2c822]增加分享管道的預設值,結果圖片類型預設值
    useEffect(() => {
        const needUpdateShareChannel = !referralShareCard.shareChannel;
        const needUpdateResultImageType = referralShareCard.resultImageType === undefined;

        if (needUpdateShareChannel || needUpdateResultImageType) {
            const defaultCard = defaultReferralShareCard(gameType).referralShareCard;
            const newReferralShareCard = { ...referralShareCard };

            if (needUpdateShareChannel) {
                newReferralShareCard.shareChannel = defaultCard.shareChannel;
            }

            if (needUpdateResultImageType) {
                newReferralShareCard.resultImageType = SHARE_CARD_IMAGE_TYPES.CUSTOM;
            }

            dispatch(
                updateSettingStyle({
                    referralShareCard: newReferralShareCard
                })
            );
        }
    }, [dispatch, referralShareCard, gameType]);

    return (
        <div css={css(resultFooterContainerStyle)}>
            {referralButton?.isShowButton && (
                <Box css={buttonsWrapperStyle} py={1} px={2}>
                    {shareSlotEnabled && (
                        <ResultPageFroalaView
                            isEditor={false}
                            content={referralShareFroalaEditor}
                        />
                    )}
                    <div css={css(shareButtonWrapperStyle)}>
                        <ShareButton
                            config={config}
                            bgColor={referralButton?.buttonBgColor}
                            text={referralButton?.buttonText}
                            textColor={referralButton?.buttonTextColor}
                            onClick={handleShareButtonClick}
                            isFetchingData={isFetchingData}
                        />
                    </div>
                    {showReferralCount && (
                        <Typography css={css(successReferralCountStyle)}>
                            {i18next.t('label.mgm_share.hint.success_join_count', {
                                count: propsReferralCount ?? referralCount
                            })}
                        </Typography>
                    )}
                </Box>
            )}
        </div>
    );
}

export default React.memo(ResultFooter);

const successReferralCountStyle = css`
    font-size: 12px;
    line-height: 17px;
    color: ${promo500.color};
    text-align: center;
    padding-top: 4px;
`;

const buttonsWrapperStyle = css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;
